import { useState } from "react"

import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"
import formatDate from "@utils/formatDate"

import { Avatar, IconButton } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import { AlertBadgeProps } from "@molecules/AlertBadge/AlertBadge.types"
import AlertBanner from "@molecules/AlertBanner/AlertBanner"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import { Prompt } from "@molecules/index"

import { MenuContentNode } from "@organisms/Menu/Menu.types"
import { Menu } from "@organisms/index"

import {
    useEcommerceCheckScopesMutation,
    useEcommerceDisconnectMutation,
    useEcommerceRegisterWebhooksMutation,
    useEcommerceTestConnectionMutation,
} from "@pages/Settings/SettingsEcommerce/SettingsEcommerce.store"
import EcommerceConnectDialog from "@pages/Settings/SettingsEcommerce/components/EcommerceConnectDialog/EcommerceConnectDialog"

import styles from "./EcommerceConnectionItem.module.scss"
import { EcommerceConnectionItemProps } from "./EcommerceConnectionItem.types"

function getConnectionStatus(connection: EcommerceConnection): {
    status: AlertBadgeProps["status"]
    label: string
} {
    if (connection.has_connection_error) {
        return { status: "error", label: "Error" }
    } else if (connection.is_connected) {
        return { status: "success", label: "Connected" }
    } else {
        return { status: "neutral", label: "Disconnected" }
    }
}

export default function EcommerceConnectionItem(props: EcommerceConnectionItemProps) {
    const { platform, connection } = props

    const { user } = useUser()

    const testConnectionMutation = useEcommerceTestConnectionMutation()
    const registerWebhooksMutation = useEcommerceRegisterWebhooksMutation()
    const checkScopesMutation = useEcommerceCheckScopesMutation()
    const disconnectMutation = useEcommerceDisconnectMutation()

    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false)
    const [isDisconnectPromptOpen, setIsDisconnectPromptOpen] = useState(false)

    const { status, label } = getConnectionStatus(connection)

    const lastVerified = connection.last_verified_at
        ? formatDate({
              datetime: connection.last_verified_at,
              preferredTimezone: user?.service_company?.preferred_timezone,
              addYear: true,
          }).localDateTimeString
        : null
    const lastVerifiedText = lastVerified ? `Last verified ${lastVerified}` : "Never verified"
    const storeIdentity = connection.platform_store_name
        ? `${connection.platform_store_name} (${connection.store_identifier})`
        : connection.store_identifier
    const metadataParts = [storeIdentity, lastVerifiedText]
    if (connection.customer_name) {
        metadataParts.push(`Ordering as ${connection.customer_name}`)
    }
    const metadataCaption = metadataParts.join(" • ")

    const handleDisconnectConfirm = () => {
        setIsDisconnectPromptOpen(false)
        disconnectMutation.mutate(connection)
    }

    const isActionPending =
        testConnectionMutation.isPending ||
        registerWebhooksMutation.isPending ||
        checkScopesMutation.isPending ||
        disconnectMutation.isPending

    const menuItems: MenuContentNode[] = [
        {
            type: "group",
            items: [
                {
                    label: "Test connection",
                    icon: faIcon({ name: "arrows-rotate", style: "regular" }),
                    onClick: () => testConnectionMutation.mutate(connection),
                },
                {
                    label: "Update credentials",
                    icon: faIcon({ name: "key", style: "regular" }),
                    onClick: () => setIsUpdateDialogOpen(true),
                },
                {
                    label: "Register webhooks",
                    icon: faIcon({ name: "bell", style: "regular" }),
                    onClick: () => registerWebhooksMutation.mutate(connection),
                },
                {
                    label: "Check permissions",
                    icon: faIcon({ name: "circle-check", style: "regular" }),
                    onClick: () => checkScopesMutation.mutate(connection),
                },
            ],
        },
        { type: "divider" },
        {
            type: "group",
            items: [
                {
                    variant: "danger",
                    label: "Disconnect",
                    icon: faIcon({ name: "trash-can", style: "regular" }),
                    onClick: () => setIsDisconnectPromptOpen(true),
                },
            ],
        },
    ]

    return (
        <>
            <div className={styles.base}>
                <ListItem.Root>
                    <ListItem.AvatarAndContent
                        avatar={
                            <Avatar
                                variant="company"
                                size="xl"
                                src={connection.platform_store_logo_url || undefined}
                                fallbackName={connection.store_name}
                            />
                        }
                        content={
                            <ListItemContent
                                leftLabel={
                                    <ListItem.Label
                                        label={connection.store_name}
                                        suffixSlot={
                                            <AlertBadge status={status} size="sm" showIcon={true} isLoading={false}>
                                                {label}
                                            </AlertBadge>
                                        }
                                    />
                                }
                                leftCaption={<ListItem.Caption caption={metadataCaption} />}
                            />
                        }
                    />
                    <ListItem.Actions>
                        <Menu
                            popoverSide="bottom"
                            popoverAlign="end"
                            title={connection.store_name}
                            trigger={
                                <IconButton
                                    variant="ghost"
                                    size="md"
                                    icon={faIcon({ name: "ellipsis", style: "regular" })}
                                    aria-label="More options"
                                    isProcessing={isActionPending}
                                    isDisabled={isActionPending}
                                />
                            }
                            items={menuItems}
                        />
                    </ListItem.Actions>
                </ListItem.Root>
                {connection.has_connection_error && connection.connection_error_details.message && (
                    <AlertBanner
                        type="card"
                        status="error"
                        message={connection.connection_error_details.message}
                        className={styles.errorBanner}
                    />
                )}
                {connection.missing_scopes.length > 0 && (
                    <AlertBanner
                        type="card"
                        status="warning"
                        message={`Missing permissions: ${connection.missing_scopes.join(", ")}.`}
                        className={styles.errorBanner}
                    />
                )}
            </div>

            <EcommerceConnectDialog
                platform={platform}
                connection={connection}
                isOpen={isUpdateDialogOpen}
                onOpenChange={setIsUpdateDialogOpen}
            />

            <Prompt
                title={`Disconnect ${connection.store_name}?`}
                body="You will no longer be able to access this store's catalog or send orders to it. You can reconnect the store later by entering new credentials."
                isOpen={isDisconnectPromptOpen}
                confirmText="Disconnect"
                onConfirm={handleDisconnectConfirm}
                cancelText="Cancel"
                onCancel={() => setIsDisconnectPromptOpen(false)}
                isDangerousOperation={true}
            />
        </>
    )
}
